import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { isSuperAdmin, useMyPermissions } from '../auth/useMyPermissions';
import { AccessDeniedPage, LoadingPage } from '../auth/AuthPages';
import { rememberDestination, takeDestination } from './intendedDestination';
import type { Profile } from '../auth/access';

/**
 * Route boundaries derive everything from the authenticated access state on
 * every load, so direct URLs and refreshes re-evaluate protection. They are
 * convenience only — real authorization is Row Level Security.
 */

function homePathFor(profile: Profile): string {
  // /home rather than /, so signing in is one navigation instead of a
  // redirect chain through the root.
  return profile.account_type === 'supplier' ? '/supplier' : '/home';
}

/**
 * Public routes (login): approved users are sent where they were going, or to
 * their home.
 *
 * The remembered destination is honoured for TAP accounts only. A supplier
 * arriving with a link to /projects/… has no business there and the page
 * would refuse them anyway; their own portal is both kinder and the
 * behaviour that existed before shared links did.
 */
export function PublicOnlyRoute() {
  const { access } = useAuth();
  if (access.status === 'loading') {
    return <LoadingPage />;
  }
  if (access.status === 'approved') {
    // takeDestination() forgets as it reads, so this fires once and a second
    // sign-in in the same tab goes home. Called during render rather than
    // from an effect deliberately: <Navigate> commits immediately, so an
    // effect would run after the redirect was already decided.
    const intended = access.profile.account_type === 'tap' ? takeDestination() : null;
    return <Navigate to={intended ?? homePathFor(access.profile)} replace />;
  }
  if (access.status === 'denied') {
    return <AccessDeniedPage reason={access.reason} />;
  }
  return <Outlet />;
}

/** Base boundary: requires an approved active profile. */
function useApprovedProfile(): { profile: Profile } | { element: ReactNode } {
  const { access } = useAuth();
  const location = useLocation();
  if (access.status === 'loading') {
    return { element: <LoadingPage /> };
  }
  if (access.status === 'signed_out') {
    // The SEARCH matters as much as the path: a shared task link is
    // /projects/<id>?task=<id>, and remembering only the pathname loses the
    // task and lands the recipient on a project page that looks like nothing
    // happened. Written here rather than in an effect because <Navigate>
    // leaves immediately; see intendedDestination.ts for why sessionStorage
    // and not router state.
    rememberDestination(`${location.pathname}${location.search}`);
    return {
      element: <Navigate to="/login" replace state={{ from: location.pathname }} />,
    };
  }
  if (access.status === 'denied') {
    return { element: <AccessDeniedPage reason={access.reason} /> };
  }
  return { profile: access.profile };
}

/** TAP boundary: approved TAP accounts only. Suppliers never mount children. */
export function RequireTap() {
  const result = useApprovedProfile();
  if ('element' in result) {
    return result.element;
  }
  if (result.profile.account_type !== 'tap') {
    return <Navigate to="/supplier" replace />;
  }
  return <Outlet context={result.profile} />;
}

/** Supplier boundary: approved supplier accounts only. */
export function RequireSupplier() {
  const result = useApprovedProfile();
  const permissions = useMyPermissions();
  if ('element' in result) {
    return result.element;
  }
  // A super admin reaches the portal to look at it as a supplier (Mimic
  // Mode). Everyone else who is not a supplier still goes home. The wait on
  // isPending matters: bouncing before the permissions land would send the
  // super admin away on every cold load and look intermittent.
  const mimicking = result.profile.account_type === 'tap' && isSuperAdmin(permissions.data);
  if (result.profile.account_type !== 'supplier' && !mimicking) {
    if (permissions.isPending) {
      return null;
    }
    return <Navigate to="/" replace />;
  }
  return <Outlet context={result.profile} />;
}

/**
 * Super Admin boundary, for the master manual at /admin/guide.
 *
 * The wait on isPending is the whole trick, and it is the same one
 * RequireSupplier documents above: isSuperAdmin() reads a permissions query
 * that has not answered yet on a cold load, so it is false for a moment for
 * everybody — including the one account this gate exists to admit. Deciding
 * during that moment locks the super admin out of their own manual on every
 * hard refresh, intermittently enough to be blamed on the network.
 *
 * Worth being plain about what this does and does not do. Row Level Security
 * protects DATA; this page is prose, so a route check is all there is. The
 * manual is therefore written to describe behaviour the database already
 * enforces — it contains no key, no bypass and no credential, and leaking it
 * would embarrass rather than endanger. Anything that would not survive being
 * read by the wrong person does not belong on a page guarded this way.
 */
export function RequireSuperAdmin() {
  const result = useApprovedProfile();
  const permissions = useMyPermissions();
  if ('element' in result) {
    return result.element;
  }
  if (permissions.isPending) {
    return <LoadingPage />;
  }
  if (result.profile.account_type !== 'tap' || !isSuperAdmin(permissions.data)) {
    return <AccessDeniedPage reason="not_admin" />;
  }
  return <Outlet context={result.profile} />;
}

/**  * TAP Admin boundary. Mounted as a sibling of RequireTap in App.tsx, not
 * nested inside it, so it re-derives the approved profile itself rather than
 * inheriting one. A supplier reaching /admin therefore gets AccessDeniedPage
 * reason="not_admin" instead of the /supplier redirect. It fails closed
 * either way. Confirmed by staging test C5 on 31 Jul 2026 — do not
 * restructure the route hierarchy to "fix" this. */
export function RequireTapAdmin() {
  const result = useApprovedProfile();
  if ('element' in result) {
    return result.element;
  }
  if (result.profile.account_type !== 'tap' || result.profile.internal_role !== 'tap_admin') {
    return <AccessDeniedPage reason="not_admin" />;
  }
  return <Outlet context={result.profile} />;
}
